import { Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, interval } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { abrirModalDeChamado } from '../abertura/abrir-chamado.modal';
import { abrirAbaDeChamados } from '../shared/abrir-aba';
import { TabService } from 'src/app/services/tab.service';
import { LanguageServices } from 'src/app/services/LanguageServices';
import { ChamadosService, TicketListFilter } from '../services/chamados.service';
import { Ticket, TicketAttachment, TicketStatus } from '../models/chamados.models';
import { ConversaBase } from '../shared/conversa-base';
import { AnexosService } from '../shared/anexos.service';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { parseBackendDate } from '../mesa/timeline';

/**
 * Balão de conversa dos chamados abertos pelo próprio usuário.
 *
 * Existe porque a tela `/chamados-meus` só funciona para quem lembra de
 * abri-la: o atendente responde e o solicitante não fica sabendo. O balão
 * inverte isso — ele aparece sozinho quando há chamado andando, e mostra
 * contador quando a resposta chegou.
 *
 * Mora no shell autenticado (`system-view-new`), e não no módulo lazy de
 * Chamados: se morasse lá dentro, só apareceria em quem já estivesse na tela
 * de chamados, que é exatamente quem não precisa dele.
 *
 * REGRA DE APARIÇÃO: só nasce quando o usuário tem chamado em andamento. Quem
 * nunca abriu um não ganha um botão flutuante para ignorar, e a tela de quem
 * não usa o módulo continua limpa.
 */
@Component({
    selector: 'chamados-widget',
    templateUrl: './chamados-widget.component.html',
    styleUrl: './chamados-widget.component.scss',
    standalone: false,
})
export class ChamadosWidgetComponent extends ConversaBase implements OnInit {
    /**
     * O container que rola na conversa. A base usa para descer ate a ultima
     * mensagem; o `#corpoDaConversa` esta no template.
     *
     * `read: ElementRef` NAO e opcional: a referencia esta num `<nz-spin>`, e
     * sem isso o ViewChild devolveria a INSTANCIA do componente — que nao tem
     * `nativeElement`, e a rolagem falharia em silencio.
     */
    @ViewChild('corpoDaConversa', { read: ElementRef })
    protected corpoDaConversa: ElementRef<HTMLElement>;


    /** Chamados em andamento do próprio usuário. */
    meusChamados: Ticket[] = [];

    aberto = false;
    carregando = false;

    /** Chamado aberto na conversa. null = mostrando a lista. */
    selecionado: Ticket = null;

    textoResposta = '';
    enviando = false;

    /** Rota atual, para o balão sumir dentro do próprio módulo. */
    private rotaAtual = '';

    /**
     * Segundos entre verificações. Um minuto: o balão é aviso, não chat em
     * tempo real, e uma consulta por minuto por usuário logado já é o teto do
     * que vale gastar para isso.
     */
    /**
     * De quantos em quantos segundos o balao se atualiza.
     *
     * Vem da configuracao da central (`intervaloAtualizacaoSegundos`); 30s e o
     * padrao enquanto ela nao chega. Era 60 fixo, e 60 e muito para quem esta
     * conversando: a resposta do atendente levava um minuto para aparecer.
     */
    private intervaloSegundos = 30;

    private readonly statusEmAndamento: TicketStatus[] = [
        'BOT',
        'AGUARDANDO_ATENDIMENTO',
        'EM_ATENDIMENTO',
        'AGUARDANDO_CLIENTE',
        'PAUSADO',
        'RESOLVIDO',
    ];

    /** Prefixo da marca de leitura no localStorage. */
    private readonly chaveVisto = 'tkt.widget.visto.';

    constructor(
        private service: ChamadosService,
        private message: NzMessageService,
        private modal: NzModalService,
        private translate: TranslateService,
        private router: Router,
        private tabService: TabService,
        private anexosService: AnexosService,
        ls: LanguageServices,
        private destroyRef: DestroyRef
    ) {
        super(ls, service);
    }

    ngOnInit(): void {

        this.rotaAtual = this.router.url;

        this.router.events
            .pipe(
                filter(evento => evento instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((evento: NavigationEnd) => this.rotaAtual = evento.urlAfterRedirects);

        this.carregar();

        // A configuracao decide o ritmo. Uma leitura so, no inicio: o
        // intervalo nao muda no meio da sessao, e reler a cada tique seria
        // gastar uma consulta para descobrir de quanto em quanto consultar.
        this.service.opcoesDeAbertura().subscribe({
            next: atendimentos => {

                const segundos = atendimentos?.[0]?.config?.intervaloAtualizacaoSegundos;

                if (segundos != null && segundos > 0) {
                    this.intervaloSegundos = segundos;
                }

                this.iniciarAtualizacao();
            },
            // Sem a configuracao, o padrao: o balao nao pode deixar de
            // atualizar porque uma consulta acessoria falhou.
            error: () => this.iniciarAtualizacao(),
        });
    }

    /** O relogio do balao. Separado para poder comecar depois da configuracao. */
    private iniciarAtualizacao(): void {

        interval(this.intervaloSegundos * 1000)
            .pipe(
                // Pula o tique quando a consulta anterior ainda não voltou:
                // numa rede lenta as chamadas se empilhariam.
                filter(() => !this.carregando),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.carregar());
    }

    carregar(): void {

        this.carregando = true;

        const listFilter: TicketListFilter = {
            somenteDoSolicitante: true,
            status: this.statusEmAndamento,
        };

        this.service.listTickets(listFilter, 0, 20, { field: 'ULTIMA_MENSAGEM', desc: true })
            .pipe(
                // `finalize`: o polling tem `filter(() => !this.carregando)`, e
                // uma requisicao que termine sem next nem error deixaria a flag
                // presa — o balao pararia de se atualizar para sempre.
                finalize(() => this.carregando = false)
            )
            .subscribe({
                next: resultado => {
                    this.meusChamados = resultado.rows;
                    this.ressincronizarSelecionado();
                },
                error: () => {
                    // Silêncio de propósito: o balão é acessório. Erro de rede
                    // aqui não pode virar um alerta vermelho por cima da tela
                    // em que a pessoa está trabalhando.
                },
            });
    }

    /**
     * O balão aparece?
     *
     * Não aparece dentro do próprio módulo de chamados — ali as telas cheias
     * já fazem tudo o que ele faria, e o balão só cobriria conteúdo.
     */
    get visivel(): boolean {

        if (this.rotaAtual != null && this.rotaAtual.indexOf('/chamados') >= 0) {
            return false;
        }

        return this.meusChamados.length > 0;
    }

    /** Chamados com resposta que o usuário ainda não viu. */
    get naoLidos(): number {
        return this.meusChamados.filter(chamado => this.temNovidade(chamado)).length;
    }

    /**
     * Tem resposta nova neste chamado?
     *
     * "Nova" é: a última mensagem não é do próprio usuário e chegou depois da
     * última vez que ele abriu este chamado. A marca é por chamado e vive no
     * navegador — é conveniência de leitura, não estado de negócio, e por isso
     * não vai para o banco.
     */
    temNovidade(chamado: Ticket): boolean {

        const ultima = chamado?.ultimaMensagem;

        if (ultima == null || ultima.autorTipo === 'CLIENTE') {
            return false;
        }

        const quando = parseBackendDate(ultima.em);

        if (quando == null) {
            return false;
        }

        const visto = this.lerVisto(chamado._id);

        return visto == null || quando.getTime() > visto;
    }

    private lerVisto(ticketId: string): number {

        try {
            const valor = localStorage.getItem(this.chaveVisto + ticketId);
            return valor != null ? Number(valor) : null;

        } catch (erro) {
            // Navegador com armazenamento bloqueado: sem marca de leitura, o
            // contador fica sempre aceso. É pior que o ideal, e melhor que
            // derrubar o balão.
            return null;
        }
    }

    private marcarVisto(chamado: Ticket): void {

        const quando = parseBackendDate(chamado?.ultimaMensagem?.em);

        if (quando == null) {
            return;
        }

        try {
            localStorage.setItem(this.chaveVisto + chamado._id, String(quando.getTime()));
        } catch (erro) {
            // Ver lerVisto: falhar em gravar não pode impedir de ler a conversa.
        }
    }

    alternar(): void {

        this.aberto = !this.aberto;

        if (this.aberto) {
            this.carregar();
        } else {
            this.fecharConversa();
        }
    }

    fechar(): void {
        this.aberto = false;
        this.fecharConversa();
    }

    // ─────────────── conversa ───────────────

    abrirConversa(chamado: Ticket): void {

        this.selecionado = chamado;
        this.timeline = [];
        this.textoResposta = '';
        this.anexos = [];
        this.marcarVisto(chamado);
        this.carregarConversa(chamado);
    }

    fecharConversa(): void {
        this.selecionado = null;
        this.timeline = [];
        this.textoResposta = '';
        this.anexos = [];
    }

    /**
     * @param silencioso refresh automatico: sem spinner e sem puxar a rolagem.
     */

    /** Anexos escolhidos para a proxima mensagem. */
    anexos: TicketAttachment[] = [];

    enviandoAnexo = false;

    /** Uploads em andamento. Zero = pode mandar a mensagem. */
    private uploadsPendentes = 0;

    /**
     * Sobe o arquivo assim que ele e escolhido — e nao no envio: a pessoa ve o
     * arquivo aceito (ou o erro) enquanto ainda escreve.
     */
    aoEscolherAnexo = (arquivo: NzUploadFile): boolean => {

        const bruto = (arquivo as unknown as File);

        if (this.selecionado == null) {
            return false;
        }

        if (!this.anexosService.cabe(bruto)) {
            this.message.error(this.ls.instant('tkt.anexo.grande'));
            return false;
        }

        this.enviandoAnexo = true;

        // Contador, e nao um booleano: escolher tres arquivos de uma vez chama
        // este metodo tres vezes, e a mensagem so pode sair quando o ULTIMO
        // terminar — senao os dois primeiros iriam sozinhos, cada um na sua
        // mensagem.
        this.uploadsPendentes++;

        const pasta = this.anexosService.pastaDoChamado(
            this.selecionado.numero, this.selecionado._id);

        this.anexosService.enviar(bruto, pasta).subscribe({
            next: anexo => {
                this.anexos.push(anexo);
                this.terminouUmUpload();
            },
            error: () => {
                this.message.error(this.ls.instant('tkt.anexo.erro'));
                this.terminouUmUpload();
            },
        });

        return false;
    };

    /**
     * Fecha um upload e, quando todos terminam, ENVIA a mensagem.
     *
     * Anexar ja e a intencao de mandar: obrigar um clique em enviar depois de
     * escolher o arquivo e um passo que so existe para ser esquecido — e o
     * arquivo fica pendurado num campo que a pessoa acha que ja foi.
     *
     * O texto que estiver escrito vai junto, que e o caso comum ("segue o
     * print").
     */
    private terminouUmUpload(): void {

        this.uploadsPendentes = Math.max(0, this.uploadsPendentes - 1);

        if (this.uploadsPendentes > 0) {
            return;
        }

        this.enviandoAnexo = false;

        // Todos falharam: nao ha o que mandar.
        if (this.anexos.length === 0) {
            return;
        }

        this.enviar();
    }

    removerAnexo(indice: number): void {
        this.anexos.splice(indice, 1);
    }

    private carregarConversa(chamado: Ticket, silencioso: boolean = false): void {

        this.carregandoConversa = !silencioso;

        this.service.listMessages(chamado._id, 0, 200).subscribe({
            next: resultado => {
                this.montarTimeline(chamado, resultado.rows);
                this.rolarParaOFim(!silencioso);
                this.carregandoConversa = false;
            },
            error: () => {
                if (!silencioso) {
                    this.message.error(this.ls.instant('tkt.conversa.loadError'));
                    this.montarTimeline(chamado, []);
                }
                this.carregandoConversa = false;
            },
        });
    }

    private ressincronizarSelecionado(): void {

        if (this.selecionado == null) {
            return;
        }

        const atualizado = this.meusChamados.find(chamado => chamado._id === this.selecionado._id);

        if (atualizado == null) {
            // Saiu da lista (encerrado, por exemplo): volta para a lista em vez
            // de continuar mostrando uma conversa que não anda mais.
            this.fecharConversa();
            return;
        }

        this.selecionado = atualizado;
        this.marcarVisto(atualizado);

        // A conversa aberta acompanha o polling: e o caso mais comum do balao
        // — a pessoa deixa a janelinha aberta esperando a resposta.
        this.carregarConversa(atualizado, true);
    }

    /**
     * Mesma regra da tela cheia: a caixa só aparece nos estados em que a
     * mensagem MOVE o chamado. Ver `podeResponder` em `/chamados-meus`.
     */
    get podeResponder(): boolean {

        const status = this.selecionado?.status;

        return status === 'BOT'
            || status === 'AGUARDANDO_ATENDIMENTO'
            || status === 'EM_ATENDIMENTO'
            || status === 'AGUARDANDO_CLIENTE'
            || status === 'PAUSADO';
    }

    get podeEnviar(): boolean {
        // Texto OU anexo — mandar so o arquivo e uma mensagem legitima.
        return !this.enviando
            && (this.textoResposta.trim().length > 0 || this.anexos.length > 0);
    }

    enviar(): void {

        if (!this.podeEnviar) {
            return;
        }

        this.enviando = true;

        this.service.responderComoSolicitante(this.selecionado, this.textoResposta.trim(),
            this.anexos).subscribe({
            next: () => {
                this.enviando = false;
                this.textoResposta = '';
                this.anexos = [];
                this.carregarConversa(this.selecionado);
                this.carregar();
            },
            error: erro => {
                this.enviando = false;
                this.message.error(this.mensagemDoErro(erro) ?? this.ls.instant('tkt.conversa.enviarError'));
            },
        });
    }

    private mensagemDoErro(erro: unknown): string {

        const resposta = erro as { error?: { message?: string } };

        if (resposta?.error?.message != null && resposta.error.message.trim().length > 0) {
            return resposta.error.message;
        }

        return null;
    }

    // ─────────────── navegação ───────────────

    verTodos(): void {
        this.fechar();
        abrirAbaDeChamados(this.tabService, this.router, 'chamados-meus', 'chamados-meus', 'inbox');
    }

    /**
     * Abre o formulario num modal central, sem sair da tela em que a pessoa
     * esta — que e o motivo de o balao existir.
     */
    /**
     * @param comoContinuacao liga o novo ao que esta aberto no balao.
     */
    abrirNovo(comoContinuacao: boolean = false): void {

        const origem = comoContinuacao && this.selecionado != null
            ? { ticketId: this.selecionado._id, numero: this.selecionado.numero }
            : null;

        this.fechar();

        abrirModalDeChamado(this.modal, this.translate, origem).afterClose.subscribe(criado => {
            if (criado != null) {
                this.carregar();
            }
        });
    }

    /** Prévia da última mensagem na lista do balão. */
    resumo(chamado: Ticket): string {

        const texto = chamado?.ultimaMensagem?.texto;

        if (texto == null || texto.trim().length === 0) {
            return chamado?.assunto ?? '';
        }

        return texto;
    }
}
